
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  BriefcaseBusiness,
  MapPin,
  Users,
  Clock3,
  IndianRupee,
  Send,
  RefreshCw,
  CalendarDays,
  Building2,
  CheckCircle2,
} from "lucide-react";

import jobService from "../../services/JobService";

export default function AvailableJobs() {
  const navigate = useNavigate();

  // =====================================================
  // STATE
  // =====================================================

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [applyingJobId, setApplyingJobId] = useState(null);
  const [trainerId, setTrainerId] = useState(null);

  // =====================================================
  // API URLS
  // =====================================================

  const TRAINERS_API =
    "/api/trainers/all";

  const APPLICATION_API =
    "/api/job-applications";

  // =====================================================
  // AUTH HEADERS
  // =====================================================

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",

      ...(token && {
        Authorization: `Bearer ${token}`,
      }),
    };
  };

  // =====================================================
  // GET LOGGED-IN USER
  // =====================================================

  const getLoggedInUser = () => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error("USER PARSE ERROR:", error);
      return null;
    }
  };

  // =====================================================
  // GET TRAINER ID
  //
  // USER EMAIL
  //      ↓
  // TRAINER EMAIL
  //      ↓
  // TRAINER ID
  //
  // IMPORTANT:
  // This function must return TRAINER ID,
  // NOT USER ID.
  // =====================================================

  const getTrainerId = async () => {
    const user = getLoggedInUser();

    if (!user) {
      throw new Error("Please login first.");
    }

    const userEmail = user?.email
      ?.trim()
      .toLowerCase();

    if (!userEmail) {
      throw new Error(
        "Logged-in user email not found."
      );
    }

    const response = await fetch(
      TRAINERS_API,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    const responseText = await response.text();

    let data = null;

    try {
      data = responseText
        ? JSON.parse(responseText)
        : null;
    } catch {
      data = responseText;
    }

    if (!response.ok) {
      throw new Error(
        data?.message ||
          data?.error ||
          "Unable to fetch trainers."
      );
    }

    // -------------------------------------------------
    // NORMALIZE RESPONSE
    // -------------------------------------------------

    let trainers = [];

    if (Array.isArray(data)) {
      trainers = data;
    } else if (Array.isArray(data?.data)) {
      trainers = data.data;
    } else if (Array.isArray(data?.content)) {
      trainers = data.content;
    }

    // -------------------------------------------------
    // FIND TRAINER BY EMAIL
    // -------------------------------------------------

    const trainer = trainers.find(
      (item) =>
        item?.email
          ?.trim()
          .toLowerCase() === userEmail
    );

    if (!trainer) {
      throw new Error(
        `Trainer not found for email: ${userEmail}`
      );
    }

    console.log(
      "MATCHED TRAINER RECORD:",
      trainer
    );

    // -------------------------------------------------
    // RESOLVE ACTUAL TRAINER ID
    //
    // DO NOT use userId if trainerId exists.
    // -------------------------------------------------

    const resolvedTrainerId =
      trainer?.trainerId ??
      trainer?.trainer_id ??
      trainer?.trainerID ??
      trainer?.id;

    if (
      resolvedTrainerId === null ||
      resolvedTrainerId === undefined ||
      resolvedTrainerId === ""
    ) {
      throw new Error(
        "Trainer ID not found."
      );
    }

    const numericTrainerId =
      Number(resolvedTrainerId);

    if (
      Number.isNaN(numericTrainerId) ||
      numericTrainerId <= 0
    ) {
      throw new Error(
        `Invalid Trainer ID: ${resolvedTrainerId}`
      );
    }

    console.log(
      "RESOLVED TRAINER ID:",
      numericTrainerId
    );

    return numericTrainerId;
  };

  // =====================================================
  // GET TRAINER APPLICATIONS
  // =====================================================

  const getTrainerApplications = async (
    trainerIdValue
  ) => {
    if (!trainerIdValue) {
      throw new Error(
        "Trainer ID is required."
      );
    }

    const response = await fetch(
      `${APPLICATION_API}/trainer/${trainerIdValue}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    const responseText = await response.text();

    let data = null;

    try {
      data = responseText
        ? JSON.parse(responseText)
        : null;
    } catch {
      data = responseText;
    }

    console.log(
      "TRAINER APPLICATION API STATUS:",
      response.status
    );

    console.log(
      "TRAINER APPLICATIONS:",
      data
    );

    if (!response.ok) {
      throw new Error(
        data?.message ||
          data?.error ||
          "Unable to fetch your applications."
      );
    }

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.content)) {
      return data.content;
    }

    return [];
  };

  // =====================================================
  // FETCH AVAILABLE JOBS
  //
  // ONLY:
  // 1. APPROVED JOBS
  // 2. JOBS NOT ALREADY APPLIED BY THIS TRAINER
  // =====================================================

  const fetchAvailableJobs = async () => {
    try {
      setLoading(true);
      setError("");

      // -------------------------------------------------
      // GET TRAINER ID
      // -------------------------------------------------

      const currentTrainerId =
        await getTrainerId();

      setTrainerId(currentTrainerId);

      console.log(
        "CURRENT TRAINER ID:",
        currentTrainerId
      );

      // -------------------------------------------------
      // GET ALL JOBS
      // -------------------------------------------------

      const jobsResponse =
        await jobService.getAllJobs();

      let allJobs = [];

      if (Array.isArray(jobsResponse)) {
        allJobs = jobsResponse;
      } else if (
        Array.isArray(jobsResponse?.data)
      ) {
        allJobs = jobsResponse.data;
      } else if (
        Array.isArray(jobsResponse?.content)
      ) {
        allJobs = jobsResponse.content;
      }

      console.log(
        "ALL JOBS:",
        allJobs
      );

      // -------------------------------------------------
      // ONLY APPROVED JOBS
      // -------------------------------------------------

      const approvedJobs =
        allJobs.filter(
          (job) =>
            String(
              job?.status || ""
            ).toUpperCase() ===
            "APPROVED"
        );

      console.log(
        "APPROVED JOBS:",
        approvedJobs
      );

      // -------------------------------------------------
      // GET TRAINER APPLICATIONS
      // -------------------------------------------------

      const applications =
        await getTrainerApplications(
          currentTrainerId
        );

      // -------------------------------------------------
      // GET ALREADY APPLIED JOB IDS
      // -------------------------------------------------

      const appliedJobIds =
        new Set(
          applications
            .filter(
              (application) =>
                application?.isActive !== false
            )
            .map(
              (application) =>
                Number(
                  application?.jobId
                )
            )
            .filter(
              (id) =>
                !Number.isNaN(id)
            )
        );

      console.log(
        "ALREADY APPLIED JOB IDS:",
        [...appliedJobIds]
      );

      // -------------------------------------------------
      // REMOVE ALREADY APPLIED JOBS
      // -------------------------------------------------

      const availableJobs =
        approvedJobs.filter(
          (job) =>
            !appliedJobIds.has(
              Number(job?.jobId)
            )
        );

      setJobs(availableJobs);

      console.log(
        "FINAL AVAILABLE JOBS:",
        availableJobs
      );
    } catch (err) {
      console.error(
        "AVAILABLE JOBS ERROR:",
        err
      );

      setError(
        err?.message ||
          "Unable to load available jobs."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    fetchAvailableJobs();
  }, []);

  // =====================================================
  // APPLY FOR JOB
  //
  // IMPORTANT:
  //
  // We DO NOT send partnerId from frontend.
  //
  // Backend should determine:
  //
  // jobId
  //   ↓
  // Job.postedBy
  //   ↓
  // Partner ID
  //
  // Therefore request contains:
  //
  // jobId
  // trainerId
  // coverLetter
  // resumeName
  // remarks
  // =====================================================

const handleApplyJob = async (job) => {
  if (!job?.jobId) {
    alert("Job ID not found.");
    return;
  }

  if (applyingJobId === job.jobId) {
    return;
  }

  const user = getLoggedInUser();

  if (!user) {
    alert("Please login first.");
    navigate("/login");
    return;
  }

  let currentTrainerId = trainerId;

  try {
    if (!currentTrainerId) {
      currentTrainerId = await getTrainerId();
      setTrainerId(currentTrainerId);
    }
  } catch (error) {
    console.error("TRAINER IDENTIFICATION ERROR:", error);

    alert(
      error?.message ||
        "Unable to identify trainer."
    );

    return;
  }

  const numericTrainerId = Number(currentTrainerId);
  const numericJobId = Number(job.jobId);

  if (
    !numericTrainerId ||
    Number.isNaN(numericTrainerId)
  ) {
    alert("Valid Trainer ID could not be identified.");
    return;
  }

  if (
    !numericJobId ||
    Number.isNaN(numericJobId)
  ) {
    alert("Valid Job ID could not be identified.");
    return;
  }

  const confirmed = window.confirm(
    `Are you sure you want to apply for "${job.jobTitle}"?`
  );

  if (!confirmed) {
    return;
  }

  try {
    setApplyingJobId(job.jobId);

    /*
     * IMPORTANT
     * --------------------------------------------------
     * DO NOT SEND:
     *
     * partnerId
     * userId
     *
     * Backend will determine:
     *
     * jobId
     *   ↓
     * Job.postedBy
     *   ↓
     * Partner
     *
     * Therefore only Job ID + Trainer ID are required.
     */

    const requestBody = {
      jobId: numericJobId,
      trainerId: numericTrainerId,
      coverLetter:
        "I am interested in this training opportunity.",
      resumeName:
        user?.resumeName || "Trainer_Resume.pdf",
      remarks:
        "Available immediately",
    };

    console.log(
      "========== APPLY JOB =========="
    );

    console.log(
      "JOB ID:",
      numericJobId
    );

    console.log(
      "TRAINER ID:",
      numericTrainerId
    );

    console.log(
      "PARTNER ID:",
      "NOT SENT"
    );

    console.log(
      "REQUEST BODY:",
      requestBody
    );

    const response = await fetch(
      APPLICATION_API,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      }
    );

    const responseText = await response.text();

    let data = null;

    try {
      data = responseText
        ? JSON.parse(responseText)
        : null;
    } catch {
      data = responseText;
    }

    console.log(
      "APPLY RESPONSE STATUS:",
      response.status
    );

    console.log(
      "APPLY RESPONSE:",
      data
    );

    if (!response.ok) {
      let message =
        "Unable to apply for this job.";

      if (
        data &&
        typeof data === "object"
      ) {
        message =
          data.message ||
          data.error ||
          data.detail ||
          message;
      } else if (
        typeof data === "string" &&
        data.trim()
      ) {
        message = data;
      }

      throw new Error(
        `${message} (HTTP ${response.status})`
      );
    }

    console.log(
      "APPLICATION CREATED SUCCESSFULLY:",
      data
    );

    /*
     * Remove applied job immediately
     */
    setJobs((previousJobs) =>
      previousJobs.filter(
        (item) =>
          Number(item?.jobId) !==
          numericJobId
      )
    );

    alert(
      "Application submitted successfully.\n\nStatus: PENDING"
    );

  } catch (error) {
    console.error(
      "APPLY JOB ERROR:",
      error
    );

    alert(
      error?.message ||
        "Unable to apply for this job."
    );
  } finally {
    setApplyingJobId(null);
  }
};
  // =====================================================
  // FORMAT SALARY
  // =====================================================

  const formatSalary = (
    min,
    max
  ) => {
    if (
      min == null &&
      max == null
    ) {
      return "Budget not specified";
    }

    if (
      min != null &&
      max != null
    ) {
      return `₹${Number(
        min
      ).toLocaleString(
        "en-IN"
      )} - ₹${Number(
        max
      ).toLocaleString(
        "en-IN"
      )}`;
    }

    if (min != null) {
      return `₹${Number(
        min
      ).toLocaleString(
        "en-IN"
      )}+`;
    }

    return `Up to ₹${Number(
      max
    ).toLocaleString(
      "en-IN"
    )}`;
  };

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (
    date
  ) => {
    if (!date) {
      return "-";
    }

    const parsedDate =
      new Date(date);

    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return "-";
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="available-jobs-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="available-jobs-header">

        <div className="available-jobs-title-row">

          <div className="available-jobs-icon">
            <BriefcaseBusiness
              size={21}
            />
          </div>

          <div>
            <h1>
              Available Jobs
            </h1>

            <p>
              Browse approved job opportunities
            </p>
          </div>

        </div>

        <button
          type="button"
          className="available-jobs-refresh"
          onClick={
            fetchAvailableJobs
          }
          disabled={loading}
        >
          <RefreshCw
            size={16}
            className={
              loading
                ? "available-jobs-spin"
                : ""
            }
          />

          Refresh
        </button>

      </div>

      {/* =================================================
          ERROR
      ================================================= */}

      {error && (
        <div className="available-jobs-error">

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              fetchAvailableJobs
            }
          >
            Try Again
          </button>

        </div>
      )}

      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (
        <div className="available-jobs-loading">

          <RefreshCw
            size={30}
            className="available-jobs-spin"
          />

          <p>
            Loading available jobs...
          </p>

        </div>
      ) : jobs.length === 0 ? (
        <div className="available-jobs-empty">

          <div className="available-empty-icon">

            <CheckCircle2
              size={42}
            />

          </div>

          <h3>
            No Available Jobs
          </h3>

          <p>
            There are currently no new approved jobs available.
          </p>

          <button
            type="button"
            onClick={
              fetchAvailableJobs
            }
          >
            <RefreshCw
              size={14}
            />

            Refresh Jobs
          </button>

        </div>
      ) : (
        <div className="available-jobs-grid">

          {jobs.map((job) => (

            <div
              className="available-job-card"
              key={job.jobId}
            >

              {/* =================================================
                  CARD HEADER
              ================================================= */}

              <div className="available-job-card-header">

                <div className="available-job-card-title">

                  <div className="available-job-card-icon">

                    <BriefcaseBusiness
                      size={19}
                    />

                  </div>

                  <div>

                    <h2>
                      {job.jobTitle ||
                        "Untitled Job"}
                    </h2>

                    <span>
                      Job ID:{" "}
                      {job.jobId || "-"}
                    </span>

                  </div>

                </div>

                <span className="available-approved-badge">
                  APPROVED
                </span>

              </div>

              {/* =================================================
                  DESCRIPTION
              ================================================= */}

              {job.jobDescription && (
                <p className="available-job-description">
                  {job.jobDescription}
                </p>
              )}

              {/* =================================================
                  DETAILS
              ================================================= */}

              <div className="available-job-details">

                {/* LOCATION */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <MapPin
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Location
                    </span>

                    <strong>
                      {job.location ||
                        "-"}
                    </strong>

                  </div>

                </div>

                {/* EMPLOYMENT */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <Building2
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Employment
                    </span>

                    <strong>
                      {job.employeeType ||
                        "-"}
                    </strong>

                  </div>

                </div>

                {/* EXPERIENCE */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <Clock3
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Experience
                    </span>

                    <strong>
                      {job.experienceRequired ||
                        "-"}
                    </strong>

                  </div>

                </div>

                {/* VACANCIES */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <Users
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Vacancies
                    </span>

                    <strong>
                      {job.vacancy ?? 0}
                    </strong>

                  </div>

                </div>

                {/* BUDGET */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <IndianRupee
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Budget
                    </span>

                    <strong>
                      {formatSalary(
                        job.salaryMin,
                        job.salaryMax
                      )}
                    </strong>

                  </div>

                </div>

                {/* DEADLINE */}

                <div className="available-job-detail">

                  <div className="available-detail-icon">

                    <CalendarDays
                      size={15}
                    />

                  </div>

                  <div>

                    <span>
                      Deadline
                    </span>

                    <strong>
                      {formatDate(
                        job.applicationDeadline
                      )}
                    </strong>

                  </div>

                </div>

              </div>

              {/* =================================================
                  SKILLS
              ================================================= */}

              <div className="available-job-skills-section">

                <span className="available-job-section-label">
                  Required Skills
                </span>

                <div className="available-job-skills">

                  {(job.skillsRequired || "")
                    .split(",")
                    .map(
                      (
                        skill,
                        index
                      ) => {

                        const cleanSkill =
                          skill.trim();

                        if (
                          !cleanSkill
                        ) {
                          return null;
                        }

                        return (
                          <span
                            key={index}
                            className="available-skill-tag"
                          >
                            {cleanSkill}
                          </span>
                        );
                      }
                    )}

                  {(!job.skillsRequired ||
                    !job.skillsRequired.trim()) && (
                    <span className="available-no-skill">
                      No skills specified
                    </span>
                  )}

                </div>

              </div>

              {/* =================================================
                  FOOTER
              ================================================= */}

              <div className="available-job-card-footer">

                <div className="available-job-company">

                  {job.companyName ? (
                    <>
                      <span>
                        Posted by
                      </span>

                      <strong>
                        {job.companyName}
                      </strong>
                    </>
                  ) : (
                    <>
                      <span>
                        Job Opportunity
                      </span>

                      <strong>
                        CertED Marketplace
                      </strong>
                    </>
                  )}

                </div>

                {/* APPLY BUTTON */}

                <button
                  type="button"
                  className="available-job-apply-btn"
                  onClick={() =>
                    handleApplyJob(job)
                  }
                  disabled={
                    applyingJobId ===
                    job.jobId
                  }
                >

                  {applyingJobId ===
                  job.jobId ? (
                    <>
                      <RefreshCw
                        size={14}
                        className="available-jobs-spin"
                      />

                      Applying...
                    </>
                  ) : (
                    <>
                      <Send
                        size={15}
                      />

                      Apply Now
                    </>
                  )}

                </button>

              </div>

            </div>

          ))}

        </div>
      )}

      <style>
        {availableJobsCSS}
      </style>

    </div>
  );
}

// =========================================================
// CSS
// =========================================================

const availableJobsCSS = `

.available-jobs-page {
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  background: #f4f7fb;
  box-sizing: border-box;
}

.available-jobs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 22px;
}

.available-jobs-title-row {
  display: flex;
  align-items: center;
  gap: 11px;
}

.available-jobs-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
}

.available-jobs-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 23px;
  font-weight: 700;
}

.available-jobs-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.available-jobs-refresh {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #dbe3ef;
  border-radius: 7px;
  background: #ffffff;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.available-jobs-refresh:hover {
  background: #eff6ff;
}

.available-jobs-refresh:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.available-jobs-grid {
  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.available-job-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px;
  box-shadow:
    0 4px 14px
    rgba(15, 23, 42, .05);
  box-sizing: border-box;
  transition:
    transform .2s ease,
    box-shadow .2s ease,
    border-color .2s ease;
}

.available-job-card:hover {
  transform: translateY(-2px);
  border-color: #bfdbfe;
  box-shadow:
    0 8px 22px
    rgba(37, 99, 235, .08);
}

.available-job-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.available-job-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.available-job-card-icon {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
}

.available-job-card-title h2 {
  margin: 0;
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
}

.available-job-card-title span {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 9px;
}

.available-approved-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 20px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #15803d;
  font-size: 8px;
  font-weight: 800;
}

.available-job-description {
  margin: 0 0 15px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.available-job-details {
  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 13px;
  background: #f8fafc;
  border-radius: 10px;
}

.available-job-detail {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  min-width: 0;
}

.available-detail-icon {
  width: 27px;
  height: 27px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.available-job-detail span {
  display: block;
  margin-bottom: 2px;
  color: #94a3b8;
  font-size: 8px;
}

.available-job-detail strong {
  display: block;
  color: #334155;
  font-size: 10px;
  line-height: 1.4;
  word-break: break-word;
}

.available-job-skills-section {
  margin-top: 14px;
}

.available-job-section-label {
  display: block;
  margin-bottom: 7px;
  color: #475569;
  font-size: 10px;
  font-weight: 700;
}

.available-job-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.available-skill-tag {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 7px;
  border-radius: 5px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 9px;
  font-weight: 600;
}

.available-no-skill {
  color: #94a3b8;
  font-size: 9px;
}

.available-job-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: auto;
  padding-top: 15px;
  border-top: 1px solid #edf2f7;
}

.available-job-company {
  min-width: 0;
}

.available-job-company span {
  display: block;
  color: #94a3b8;
  font-size: 8px;
}

.available-job-company strong {
  display: block;
  margin-top: 2px;
  color: #334155;
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.available-job-apply-btn {
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
  flex-shrink: 0;
  border: 1px solid #2563eb;
  border-radius: 7px;
  background: #2563eb;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all .2s ease;
}

.available-job-apply-btn:hover {
  background: #1d4ed8;
  border-color: #1d4ed8;
  transform: translateY(-1px);
}

.available-job-apply-btn:disabled {
  opacity: .65;
  cursor: not-allowed;
  transform: none;
}

.available-jobs-loading {
  min-height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.available-jobs-loading p {
  margin-top: 10px;
  font-size: 12px;
}

.available-jobs-empty {
  min-height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #94a3b8;
}

.available-empty-icon {
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
}

.available-jobs-empty h3 {
  margin: 14px 0 5px;
  color: #334155;
  font-size: 18px;
}

.available-jobs-empty p {
  margin: 0 0 15px;
  font-size: 12px;
}

.available-jobs-empty button {
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 1px solid #dbe3ef;
  border-radius: 7px;
  background: #ffffff;
  color: #2563eb;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.available-jobs-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 15px;
  padding: 11px 14px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
}

.available-jobs-error button {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.available-jobs-spin {
  animation:
    availableJobsSpin
    1s linear infinite;
}

@keyframes availableJobsSpin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .available-jobs-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .available-jobs-page {
    padding: 15px;
  }

  .available-jobs-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .available-jobs-grid {
    grid-template-columns: 1fr;
  }

  .available-job-details {
    grid-template-columns: 1fr;
  }

  .available-job-card-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .available-job-apply-btn {
    width: 100%;
  }
}

`;
